import React from "react";

const ClassDetailModal = ({
  isOpen,
  onClose,
  classItem,
  contents = [],
  onGetStarted,
  contentsLoading = false,
}) => {
  if (!isOpen || !classItem) return null;
  // Calculate stats from contents
  const totalDuration = contents.reduce(
    (sum, content) => sum + (content.duration || 0),
    0
  );
  const missions = contents.filter(
    (content) =>
      content.content_type_name &&
      (content.content_type_name.toLowerCase().includes("mission") ||
        content.content_type_name.toLowerCase().includes("task"))
  ).length;
  const quizzes = contents.filter(
    (content) =>
      content.content_type_name &&
      content.content_type_name.toLowerCase().includes("quiz")
  ).length;

  const handleGetStarted = () => {
    onGetStarted();
    onClose();
  };

  return (
    <div className="class-detail-modal-overlay" onClick={onClose}>
      <div
        className="class-detail-modal-container"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="class-detail-modal-header">
          <button className="class-detail-close-button" onClick={onClose}>
            <span className="class-detail-close-icon">×</span>
          </button>
        </div>

        <div className="class-detail-modal-content">
          <div className="class-info-section">
            <h2 className="class-detail-class-title">{classItem.name}</h2>

            {classItem.description && (
              <div className="class-description">
                {classItem.description.split("\n").map((line, index) => {
                  // Check if line contains bullet points or starts with common bullet symbols
                  const bulletMatch = line.match(/^[\s]*[•·▪▫‣⁃-]\s*(.+)/);
                  if (bulletMatch) {
                    return (
                      <div key={index} className="description-bullet-point">
                        <span className="bullet-symbol">•</span>
                        <span className="bullet-text">
                          {bulletMatch[1].trim()}
                        </span>
                      </div>
                    );
                  }
                  // Check if line starts with numbers (like "1. text" or "1) text")
                  const numberedMatch = line.match(/^[\s]*(\d+)[.)]\s*(.+)/);
                  if (numberedMatch) {
                    return (
                      <div key={index} className="description-numbered-point">
                        <span className="number-symbol">
                          {numberedMatch[1]}.
                        </span>
                        <span className="number-text">
                          {numberedMatch[2].trim()}
                        </span>
                      </div>
                    );
                  }
                  // Regular text line
                  return line.trim() ? (
                    <p key={index} className="description-text">
                      {line.trim()}
                    </p>
                  ) : null;
                })}
              </div>
            )}

            <div className="stats-container">
              <div className="stat-item">
                <span className="stat-icon clock-icon">🕐</span>
                <span className="stat-label">Duration:</span>
                <span className="stat-value">{totalDuration || 45}mins</span>
              </div>

              <div className="stat-item">
                <span className="stat-icon target-icon">🎯</span>
                <span className="stat-label">Missions:</span>
                <span className="stat-value">{missions || 3}</span>
              </div>

              <div className="stat-item">
                <span className="stat-icon quiz-icon">❓</span>
                <span className="stat-label">Quiz:</span>
                <span className="stat-value">{quizzes || 1}</span>
              </div>

              <div className="stat-item">
                <span className="stat-icon award-icon">🏆</span>
                <span className="stat-label">ECTS:</span>
                <span className="stat-value">
                  {classItem.has_ects ? "Yes" : "No"}
                </span>
              </div>
            </div>
          </div>

          <div className="class-detail-modules-section">
            <h3 className="class-detail-modules-title">Index:</h3>

            {contentsLoading ? (
              <div className="loading-state">Loading modules...</div>
            ) : contents && contents.length > 0 ? (
              <div className="class-detail-modules-grid">
                {contents.map((content, index) => (
                  <div key={content.id} className="class-detail-module-item">
                    {content.title}
                  </div>
                ))}
              </div>
            ) : (
              <div className="no-modules">No modules available</div>
            )}
          </div>

          <button
            className="class-detail-get-started-button"
            onClick={handleGetStarted}
          >
            Get Started
            <span className="button-arrow">➤</span>
          </button>
        </div>

        <style jsx>{`
          .class-detail-modal-overlay {
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background-color: rgba(0, 0, 0, 0.5);
            display: flex;
            align-items: center;
            justify-content: center;
            z-index: 1000;
            padding: 20px;
          }

          .class-detail-modal-container {
            background: linear-gradient(135deg, #f5f1eb 0%, #e8ddd4 100%);
            border-radius: 20px;
            width: 100%;
            max-width: 600px;
            max-height: 90vh;
            overflow-y: auto;
            position: relative;
            box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
          }

          .class-detail-modal-header {
            display: flex;
            justify-content: flex-end;
            padding: 20px 20px 0 20px;
          }

          .class-detail-close-button {
            background: none;
            border: none;
            cursor: pointer;
            color: #6b5b47;
            padding: 8px;
            border-radius: 50%;
            transition: background-color 0.2s;
          }

          .class-detail-close-button:hover {
            background-color: rgba(107, 91, 71, 0.1);
          }

          .close-icon {
            font-size: 24px;
            font-weight: bold;
            line-height: 1;
          }

          .class-detail-modal-content {
            padding: 0 40px 40px 40px;
          }

          .class-info-section {
            text-align: center;
            margin-bottom: 30px;
          }

          .class-detail-class-title {
            font-size: 28px;
            font-weight: bold;
            color: #3a2f24;
            margin-bottom: 12px;
            line-height: 1.2;
          }

          .class-description {
            color: #6b5b47;
            font-size: 16px;
            line-height: 1.5;
            margin-bottom: 24px;
            max-width: 500px;
            margin-left: auto;
            margin-right: auto;
            text-align: left;
          }

          .description-bullet-point,
          .description-numbered-point {
            display: flex;
            align-items: flex-start;
            margin-bottom: 8px;
            text-align: left;
          }

          .bullet-symbol,
          .number-symbol {
            color: #8b6f47;
            font-weight: bold;
            margin-right: 8px;
            flex-shrink: 0;
            margin-top: 2px;
          }

          .bullet-text,
          .number-text {
            flex: 1;
            line-height: 1.4;
          }

          .description-text {
            margin-bottom: 8px;
            text-align: left;
          }

          .stats-container {
            display: grid;
            grid-template-columns: repeat(2, 1fr);
            gap: 16px;
            margin: 24px 0;
          }

          .stat-item {
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 8px;
            padding: 12px;
            background: rgba(255, 255, 255, 0.3);
            border-radius: 12px;
            font-size: 14px;
          }

          .stat-icon {
            font-size: 18px;
            flex-shrink: 0;
            display: inline-block;
            width: 20px;
            text-align: center;
          }

          .stat-label {
            color: #6b5b47;
            font-weight: 500;
          }

          .stat-value {
            color: #3a2f24;
            font-weight: bold;
          }

          .class-detail-modules-section {
            margin-bottom: 30px;
          }

          .class-detail-modules-title {
            font-size: 20px;
            font-weight: bold;
            color: #3a2f24;
            margin-bottom: 16px;
          }

          .loading-state,
          .no-modules {
            text-align: center;
            color: #6b5b47;
            padding: 20px;
            font-style: italic;
          }

          .class-detail-modules-grid {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
            gap: 8px;
          }

          .class-detail-module-item {
            background: rgba(255, 255, 255, 0.4);
            padding: 12px 16px;
            border-radius: 8px;
            font-size: 14px;
            color: #3a2f24;
            border-left: 3px solid #8b6f47;
          }

          .class-detail-get-started-button {
            width: 100%;
            background: linear-gradient(135deg, #5d3221 0%, #6b5b47 100%);
            color: white;
            border: none;
            padding: 16px 24px;
            border-radius: 50px;
            font-size: 18px;
            font-weight: bold;
            cursor: pointer;
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 12px;
            transition: all 0.3s ease;
            box-shadow: 0 4px 12px rgba(139, 111, 71, 0.3);
          }

          .class-detail-get-started-button:hover {
            background: linear-gradient(135deg, #a18157 0%, #8b6f47 100%);
            transform: translateY(-2px);
            box-shadow: 0 6px 16px rgba(139, 111, 71, 0.4);
          }

          .button-arrow {
            font-size: 16px;
            transition: transform 0.3s ease;
          }

          .class-detail-get-started-button:hover .button-arrow {
            transform: translateX(4px);
          }

          @media (max-width: 768px) {
            .class-detail-modal-container {
              margin: 10px;
              max-width: none;
            }

            .class-detail-modal-content {
              padding: 0 20px 30px 20px;
            }

            .class-detail-class-title {
              font-size: 24px;
            }

            .stats-container {
              grid-template-columns: 1fr;
              gap: 12px;
            }

            .class-detail-modules-grid {
              grid-template-columns: 1fr;
            }
          }
        `}</style>
      </div>
    </div>
  );
};

export default ClassDetailModal;
